import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Cookie, Eye, Server, Trash2, Mail, Info, CheckCircle2, XCircle } from 'lucide-react';
import Section from '../components/Section';

const Privacy: React.FC = () => {
  // Animasyon Varyantları
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <>
      {/* 1. HERO SECTION: Kurumsal Başlık */}
      <section className="relative pt-32 pb-20 bg-primary-900 overflow-hidden">
        {/* Arka plan efektleri */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
            <div className="absolute top-[-10%] right-[10%] w-96 h-96 bg-blue-500 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-[-10%] left-[-10%] w-72 h-72 bg-teal-500 rounded-full blur-[100px]"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-16 h-16 bg-primary-800/50 rounded-2xl flex items-center justify-center mx-auto mb-6 text-primary-300 border border-primary-700">
               <Shield size={32} />
            </div>
            <h1 className="text-3xl md:text-5xl font-display font-bold mb-6 text-white tracking-tight">
              Gizlilik ve Çerez Politikası
            </h1>
            <p className="text-lg text-primary-100 max-w-2xl mx-auto">
              Verilerinizin güvenliği bizim için önceliklidir. Bilgilerinizi nasıl topladığımızı ve koruduğumuzu şeffaflıkla açıklıyoruz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. MAIN CONTENT */}
      <Section className="py-16 -mt-10 relative z-20">
        <motion.div 
          className="max-w-5xl mx-auto bg-white rounded-3xl shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden"
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          
          {/* BÖLÜM 1: GİZLİLİK POLİTİKASI */}
          <div className="p-8 md:p-12 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <Lock className="text-primary-600" size={28} />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Gizlilik Politikası</h2>
            </div>
            
            <div className="prose prose-lg text-gray-600 leading-relaxed">
              <p className="mb-4">
                Bu gizlilik bildirimi, <strong>Lotus Sport</strong> web sitesinin veriyi toplama ve kullanma yöntemini açıklar.
                Sizlere daha iyi hizmet verebilmek amacıyla, bu uygulamaya erişim sağladığınızda sizden bazı
                kişisel bilgilerinizi istemekteyiz. 
              </p>
              <div className="bg-primary-50 p-6 rounded-xl border border-primary-100 my-6">
                <h4 className="font-bold text-primary-900 mb-2">Toplanan Bilgiler</h4>
                <p className="text-sm">İsim, soy isim, e-posta adresi, cep telefonu vb. bilgiler yer almaktadır. Bu bilgiler sitenin iyileştirilmesi ve müşteri eğilimlerinin anlaşılması için kullanılabilir.</p>
              </div>
              <p>
                Lotus Sport, topladığı bilgileri sizin haberiniz olmadan üçüncü şahıslarla kesinlikle paylaşmaz, faaliyet dışı hiçbir
                nedenle ticari amaçla kullanmaz ve satmaz.
              </p>
            </div>
          </div>

          {/* BÖLÜM 2: ÇEREZ POLİTİKASI */}
          <div className="p-8 md:p-12 bg-gray-50/50">
            <div className="flex items-center gap-3 mb-8">
              <Cookie className="text-primary-600" size={28} />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Çerez Politikası</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Kart 1: Nedir? */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-3 text-blue-600">
                  <Info size={20} />
                  <h3 className="font-bold text-gray-900">Çerezler Nedir?</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Web sitelerinde bulunan küçük, genellikle şifrelenmiş metin dosyalarıdır. Kullanıcıların web sitelerinde verimli gezinmesine yardımcı olur. Çoğu tarayıcı destekler ancak kullanıcılar bunları reddedebilir.
                </p>
              </div>

              {/* Kart 2: Ne Yapamaz? */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-3 text-red-500">
                  <XCircle size={20} />
                  <h3 className="font-bold text-gray-900">Ne Yapamazlar?</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Çerezler düz metin dosyalarıdır; bilgisayarınıza veya kişisel verilerinize <strong>doğrudan erişemezler</strong>. Sadece sitenin (oturum açma, dil tercihi vb.) düzgün çalışmasını sağlarlar.
                </p>
              </div>

              {/* Kart 3: Nasıl Toplanır? */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-3 text-purple-600">
                  <Server size={20} />
                  <h3 className="font-bold text-gray-900">Nasıl Toplanır?</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Tarayıcıya eriştiğiniz cihazlar aracılığıyla toplanır. Bu bilgiler cihazlara özeldir ve kullanıcı tarafından istenildiğinde silinebilir veya kısıtlanabilir.
                </p>
              </div>

              {/* Kart 4: Neden Kullanıyoruz? */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-3 text-green-600">
                  <CheckCircle2 size={20} />
                  <h3 className="font-bold text-gray-900">Neden Kullanıyoruz?</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Kullanıcı deneyimini artırmak, etkileşimleri analiz etmek için kullanılır. Örneğin; dil tercihlerinizi veya sepetinizi hatırlayarak işlemlerinizi kolaylaştırır.
                </p>
              </div>
            </div>

            {/* Alt Başlıklar Listesi */}
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-bold text-primary-900 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                  Kullandığımız Çerez Türleri
                </h3>
                <ul className="list-none space-y-3 pl-4 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-gray-800 whitespace-nowrap">Oturum Çerezleri:</span>
                    <span>Tarayıcınızı kapattığınızda silinen geçici çerezlerdir.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-gray-800 whitespace-nowrap">Kalıcı Çerezler:</span>
                    <span>Belirli bir süre boyunca veya manuel olarak silinene kadar saklanır.</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold text-primary-900 mb-3 flex items-center gap-2">
                  <Trash2 className="text-gray-400" size={20} />
                  Çerezleri Nasıl Silebilirsiniz?
                </h3>
                <p className="text-gray-600 leading-relaxed mb-2">
                  Tarayıcınızın ayarlarını değiştirerek çerezlere dair tercihlerinizi yönetebilirsiniz. Bazı çerezleri reddetmeniz durumunda web sitesinin belirli işlevleri çalışmayabilir.
                </p>
                <p className="text-sm text-gray-500 italic">
                  *Bu nedenle oluşabilecek üyelik, sipariş veya aktivasyon aksaklıklarından Lotus Sport sorumlu değildir.
                </p>
              </div>
            </div>
          </div>

          {/* BÖLÜM 3: İLETİŞİM FOOTER */}
          <div className="bg-primary-900 p-8 md:p-12 text-center">
             <div className="inline-flex items-center justify-center w-12 h-12 bg-white/10 rounded-full text-white mb-4">
                <Mail size={24} />
             </div>
             <h3 className="text-xl font-bold text-white mb-2">Sorularınız mı var?</h3>
             <p className="text-primary-100 mb-6">
               Çerez Politikası veya gizlilik ile ilgili tüm soru ve görüşleriniz için bizimle iletişime geçin.
             </p>
             <a 
               href="mailto:info@lotussport.net" 
               className="inline-flex items-center gap-2 bg-white text-primary-900 px-6 py-3 rounded-xl font-bold hover:bg-primary-50 transition-colors"
             >
               info@lotussport.net
             </a>
          </div>

        </motion.div>
      </Section>
    </>
  );
};

export default Privacy;